import React, { useEffect, useState } from 'react';
import {
  HiArrowRight,
  HiArrowLeft,
  HiCheckCircle,
  HiExclamationCircle,
  HiXCircle,
} from 'react-icons/hi2';
import { getDeviceDiagnostics } from '../utils/api';

const statusUI = {
  active: {
    color: 'bg-green-100 text-green-700',
    icon: <HiCheckCircle className="w-5 h-5 text-green-600" />,
    label: 'Device Active',
    message: 'Device is online and responding.',
  },
  inactive: {
    color: 'bg-yellow-100 text-yellow-700',
    icon: <HiExclamationCircle className="w-5 h-5 text-yellow-600" />,
    label: 'Device Inactive',
    message: 'Device has not been seen recently.',
  },
  unknown: {
    color: 'bg-gray-100 text-gray-700',
    icon: <HiXCircle className="w-5 h-5 text-gray-600" />,
    label: 'Unknown',
    message: 'No signal information available.',
  },
};

export default function SignalStatusStep({ ocrData, onBack, onNext }) {
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDiagnostics = async () => {
      try {
        const res = await getDeviceDiagnostics(ocrData.eui);
        setDiagnostics(res);
      } catch (err) {
        console.error(err);
        setError('Unable to fetch device diagnostics.');
      } finally {
        setLoading(false);
      }
    };

    fetchDiagnostics();
  }, [ocrData]);

  const status = diagnostics?.status?.toLowerCase();
  const ui = statusUI[status] || statusUI.unknown;

  const formatLabel = (key) =>
    key
      .replace(/_/g, ' ')
      .replace(/\b[a-z]/g, (c) => c.toUpperCase());

  return (
    <div className="flex flex-col justify-between flex-grow space-y-6 flex-1">
      {/* Top */}
      <div className="space-y-8">
        <h1 className="text-2xl font-semibold text-gray-800">Connection Diagnostics</h1>

        {loading ? (
          <p className="text-sm text-gray-500">Checking device status...</p>
        ) : error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : (
          <>
            {/* Device Status */}
            <div className={`rounded-lg p-4 flex items-start gap-4 ${ui.color} border`}>
              <div>{ui.icon}</div>
              <div>
                <p className="text-sm font-medium">{ui.label}</p>
                <p className="text-sm">{ui.message}</p>
              </div>
            </div>

            {/* Dynamic Metrics Section */}
            <div>
              <p className="text-sm text-gray-600 mb-2">Diagnostics Metrics</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {Object.entries(diagnostics)
                  .filter(([key]) => key !== 'signalStrength')
                  .map(([key, value]) => (
                    <div
                      key={key}
                      className="border rounded-lg p-4 bg-gray-50 flex flex-col justify-between h-full"
                    >
                      <p className="text-gray-500 text-sm mb-1">{formatLabel(key)}</p>
                      <p className="font-medium text-gray-800 text-base">
                        {key === 'lastSeen' && typeof value === 'number'
                          ? new Date(value).toLocaleString()
                          : String(value)}
                      </p>
                    </div>
                  ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Navigation */}
      <div className="pt-6 flex justify-between mt-auto">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-gray-200 text-sm text-gray-800 hover:bg-gray-300 transition"
        >
          <HiArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={onNext}
          className="inline-flex items-center gap-2 px-6 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition"
        >
          Next
          <HiArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
